import { useEffect, useState } from 'react'
import { ChevronRight } from '../../../components/ChevronRight'
import { ScreenHeader } from '../../../components/ScreenHeader'
import { BackgroundTitle } from '../../../components/BackgroundTitle'
import { useAuth } from '../../../lib/auth'
import {
  buildPromptText,
  daysUntilNextMonday, fetchMyThreeStepWeeklyEntry,
  fetchLastFinalizedThreeStepWeekly, fetchThreeStepWeeklyWinners,
  getOrCreateThreeStepWeekly, thisMondayISO, weekLabel,
  type ThreeStepWeeklyEntryRow, type ThreeStepWeeklyEventRow,
  type ThreeStepWeeklyWinnerRow,
} from '../api/weekly'
import { currentWeekStarts } from '../../../lib/api/weekly'
import { readError } from '../../../lib/errors'
import { WeeklyTipOverlay } from '../../../platform/weekly/WeeklyTipOverlay'
import { useWeeklyTip } from '../../../lib/api/userFlags'
import { useT } from '../../../i18n'

interface Props {
  onPlayThisWeek: (eventId: string, entryId: string) => void
  onVoteOnWeek: (eventId: string) => void
  onBack: () => void
  onOpenArchive: () => void
}

/**
 * Three Step Solution — Weekly hub. Same shape as the Colorful + IET
 * hubs:
 *   1. This Week · Write — the prompt, a 0/3 progress chip, a CTA.
 *   2. Last Week · Vote — entries from the most-recent finalized week
 *      ready to read and upvote.
 *   3. Archive — flat list of every past week.
 *
 * The prompt for the week is server-generated from the action pool;
 * we just render it here.
 */
export function ThreeStepWeeklyHubScreen({
  onPlayThisWeek, onVoteOnWeek, onBack, onOpenArchive,
}: Props) {
  const t = useT()
  const { profile, session, configured } = useAuth()
  const signedIn = Boolean(session && profile)

  const [thisWeek, setThisWeek] = useState<ThreeStepWeeklyEventRow | null>(null)
  const [lastWeek, setLastWeek] = useState<ThreeStepWeeklyEventRow | null>(null)
  const [myEntry, setMyEntry] = useState<ThreeStepWeeklyEntryRow | null>(null)
  const [winners, setWinners] = useState<ThreeStepWeeklyWinnerRow[] | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const { showTip, dismissTip } = useWeeklyTip('three_step')

  useEffect(() => {
    if (!configured) { setLoading(false); return }
    let cancelled = false
    async function load() {
      try {
        const weekStart = thisMondayISO()
        // get_or_create finalizes the prior week — run first so the
        // rest of the queries see the fresh state.
        const event = await getOrCreateThreeStepWeekly(weekStart)
        if (cancelled) return
        setThisWeek(event)

        // Exclude the CURRENT VOTE WEEK from the winners list — the
        // Hub's "Last week · Vote" card already shows it, and the
        // dedicated Archive screen excludes the same value. Excluding
        // weekStart (this-week) instead would keep the vote week in
        // the Hub count but the Archive page would correctly hide it,
        // producing "1 past week" with an empty Archive on tap.
        const { lastWeek: lastWeekStart } = currentWeekStarts()
        const [last, entry, pastWinners] = await Promise.all([
          fetchLastFinalizedThreeStepWeekly(weekStart),
          signedIn && profile ? fetchMyThreeStepWeeklyEntry(event.id, profile.id) : Promise.resolve(null),
          fetchThreeStepWeeklyWinners(lastWeekStart),
        ])
        if (cancelled) return
        setLastWeek(last)
        setMyEntry(entry)
        setWinners(pastWinners)
      } catch (e) {
        if (!cancelled) setError(readError(e))
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    load()
    return () => { cancelled = true }
  }, [configured, signedIn, profile?.id])

  if (!configured) {
    return (
      <Shell onBack={onBack}>
        <div className="card p-5 mx-5">
          <p className="font-semibold text-brand-text">{t("Supabase isn't configured")}</p>
          <p className="text-sm text-brand-muted mt-1">
            {t('The weekly event needs the backend to be set up.')}
          </p>
        </div>
      </Shell>
    )
  }

  if (loading || !thisWeek) {
    return (
      <Shell onBack={onBack}>
        <div className="px-5 space-y-3">
          <div className="h-44 rounded-2xl bg-brand-elevated animate-pulse" />
          <div className="h-20 rounded-2xl bg-brand-elevated animate-pulse" />
          <div className="h-20 rounded-2xl bg-brand-elevated animate-pulse" />
        </div>
      </Shell>
    )
  }

  const stepsFilled = myEntry
    ? [myEntry.step1_text, myEntry.step2_text, myEntry.step3_text]
        .filter(s => s.trim().length > 0).length
    : 0
  const isDone = stepsFilled === 3

  return (
    <Shell onBack={onBack}>
      <div className="px-5 space-y-5 overflow-y-auto pb-6">
        {error && (
          <div className="card p-3 bg-brand-danger/10 border border-brand-danger/30">
            <p className="text-sm text-brand-danger">{error}</p>
          </div>
        )}

        {/* This week — write phase */}
        <div className="bg-game-three-step-light border-2 border-game-three-step-dark
                        rounded-2xl shadow-card p-5">
          <div className="flex items-start justify-between mb-4">
            <div>
              <p className="text-xs font-semibold text-game-three-step-dark
                            uppercase tracking-wide mb-1">
                {t('This Week · Write')}
              </p>
              <h2 className="font-display text-xl font-bold text-brand-text">
                {weekLabel(thisWeek.week_start)}
              </h2>
            </div>
            <div className="countdown-pill">
              {t('{n}d left', { n: daysUntilNextMonday(thisWeek.week_start) })}
            </div>
          </div>

          <div className="mb-4">
            <p className="text-xs font-medium text-brand-muted uppercase tracking-wide mb-2">
              {t("This week's prompt")}
            </p>
            <p className="font-display text-base font-bold text-brand-text leading-snug">
              {buildPromptText(thisWeek.action_a, thisWeek.action_b, t)}
            </p>
          </div>

          {signedIn ? (
            <button
              onClick={() => myEntry && onPlayThisWeek(thisWeek.id, myEntry.id)}
              disabled={!myEntry}
              className={isDone
                ? 'btn-three-step-outline'
                : 'btn-three-step animate-pulse-glow'}
            >
              {isDone
                ? t('Edit your three steps')
                : stepsFilled > 0
                  ? t('Continue ({n}/3)', { n: stepsFilled })
                  : t('Write your three steps')}
            </button>
          ) : (
            <p className="text-sm text-brand-muted text-center">
              {t('Sign in to submit. Guests can read, not write.')}
            </p>
          )}
        </div>

        {/* Last week — vote phase */}
        {lastWeek ? (
          <button
            onClick={() => onVoteOnWeek(lastWeek.id)}
            className="w-full bg-white border-2 border-brand-border
                       rounded-2xl shadow-card p-4 flex items-center gap-4 text-left
                       active:scale-[0.98] transition-transform"
          >
            <div className="w-12 h-12 rounded-2xl bg-game-three-step-light
                            flex items-center justify-center shrink-0">
              <span className="text-2xl">👍</span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[10px] font-semibold text-game-three-step-dark
                            uppercase tracking-widest">
                {t('Last week · Vote')}
              </p>
              <h3 className="font-semibold text-brand-text">
                {weekLabel(lastWeek.week_start)}
              </h3>
              <p className="text-xs text-brand-muted truncate">
                {buildPromptText(lastWeek.action_a, lastWeek.action_b, t)}
              </p>
            </div>
            <ChevronRight size={20} color="#0EAE93" className="shrink-0" />
          </button>
        ) : (
          <div className="card p-4 opacity-80">
            <p className="text-xs font-semibold text-brand-muted uppercase tracking-wide mb-1">
              {t('Last Week · Vote')}
            </p>
            <p className="text-sm text-brand-muted">{t('No finalized entries from last week yet. The voting card will appear here once a previous week is locked.')}</p>
          </div>
        )}

        {/* Archive — only render when there's at least one truly-past
            week, otherwise the link opens an empty page. */}
        {winners && winners.length > 0 && (
          <button
            onClick={onOpenArchive}
            className="w-full bg-game-three-step-light border-2 border-game-three-step-dark
                       rounded-2xl shadow-card p-4 flex items-center gap-3 text-left
                       active:scale-[0.98] transition-transform"
          >
            <div className="flex-1">
              <p className="font-semibold text-brand-text">{t('Weekly archive')}</p>
              <p className="text-xs text-brand-muted">
                {winners.length === 1
                  ? t('1 past week')
                  : t('{n} past weeks', { n: winners.length })}
              </p>
            </div>
            <ChevronRight color="#0EAE93" />
          </button>
        )}
      </div>

      {showTip && <WeeklyTipOverlay game="three_step" onDismiss={dismissTip} />}
    </Shell>
  )
}

function Shell({
  onBack, children,
}: {
  onBack: () => void
  children: React.ReactNode
}) {
  const t = useT()
  return (
    <div className="screen pb-24">
      <BackgroundTitle text={t('Weekly Three Step Solution')} color="#0EAE93" />
      <ScreenHeader
        image="/games/three-step.webp"
        imageAlt="Three Step Solution"
        leftSlot={
          <button
            onClick={onBack}
            className="w-9 h-9 flex items-center justify-center rounded-full
                       bg-brand-elevated active:bg-brand-border transition-colors"
            aria-label={t('Back to weekly hub')}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
              <path d="M15 18l-6-6 6-6" stroke="#111" strokeWidth="2"
                    strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
        }
      />
      <div className="relative z-10">{children}</div>
    </div>
  )
}
